import { useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

/**
 * Shared lead-capture form. Posts to /api/enquiries with the given `source`.
 * Sources: "tuitions" | "study-abroad" | "resources" | "landing"
 */
export default function EnquiryForm({ source, title, subtitle, interestPlaceholder = "Course or subject you're interested in" }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            return toast.error("Name, email and phone are required.");
        }
        setSubmitting(true);
        try {
            await api.post("/enquiries", { ...form, source });
            setDone(true);
            toast.success("Enquiry sent — we'll call you within one working day.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send enquiry — try WhatsApp instead.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="border border-ink/15 rounded-sm bg-bone p-10 text-center" data-testid={`enquiry-form-${source}-done`}>
                <CheckCircle size={44} weight="fill" className="mx-auto text-emerald-700 mb-4" />
                <div className="font-serif text-3xl text-ink">Thanks &mdash; we&apos;ve got it.</div>
                <p className="text-ink/70 mt-3 max-w-md mx-auto">
                    A Wibpro counsellor will reach out at{" "}
                    <span className="font-mono text-ink">{form.phone}</span> within one working day.
                    Meanwhile, our doors are open <span className="font-serif italic text-ink">Mon&ndash;Sat, 10 AM&ndash;7 PM IST.</span>
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="border border-ink/15 rounded-sm bg-bone p-6 md:p-8 space-y-4" data-testid={`enquiry-form-${source}`}>
            {(title || subtitle) && (
                <div>
                    {title && <div className="font-serif text-2xl text-ink">{title}</div>}
                    {subtitle && <div className="text-sm text-ink/60 mt-1">{subtitle}</div>}
                </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label className="text-xs uppercase tracking-widest text-ink/60">Your name</label>
                    <Input value={form.name} onChange={onChange("name")} className="rounded-sm mt-1" required data-testid={`enq-${source}-name`}/>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-ink/60">Phone</label>
                    <Input value={form.phone} onChange={onChange("phone")} className="rounded-sm mt-1" placeholder="+91 …" required data-testid={`enq-${source}-phone`}/>
                </div>
            </div>
            <div>
                <label className="text-xs uppercase tracking-widest text-ink/60">Email</label>
                <Input type="email" value={form.email} onChange={onChange("email")} className="rounded-sm mt-1" required data-testid={`enq-${source}-email`}/>
            </div>
            <div>
                <label className="text-xs uppercase tracking-widest text-ink/60">Interested in</label>
                <Input value={form.interest} onChange={onChange("interest")} placeholder={interestPlaceholder} className="rounded-sm mt-1" data-testid={`enq-${source}-interest`}/>
            </div>
            <div>
                <label className="text-xs uppercase tracking-widest text-ink/60">Message <span className="text-ink/40 normal-case">(optional)</span></label>
                <textarea
                    value={form.message}
                    onChange={onChange("message")}
                    rows={3}
                    className="mt-1 w-full rounded-sm border border-ink/15 bg-bone px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-ink/20"
                    placeholder="Tell us about your timeline, target score, current board…"
                    data-testid={`enq-${source}-message`}
                />
            </div>
            <div className="pt-2 flex flex-wrap items-center gap-4">
                <Button type="submit" disabled={submitting} className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-11 px-6" data-testid={`enq-${source}-submit`}>
                    <PaperPlaneTilt size={16} className="mr-2" weight="fill"/>
                    {submitting ? "Sending…" : "Send enquiry"}
                </Button>
                <span className="text-xs text-ink/50">No spam. We call once, then wait for you to call us back.</span>
            </div>
        </form>
    );
}
